import type SQLite from 'react-native-sqlite-storage';
import type { Transaction } from 'react-native-sqlite-storage';
import { OFFLINE_DATABASE_VERSION } from './offlineDatabase.connection';
import { addColumnIfMissing, getPendingMigrationVersions, tableExists } from './offlineDatabase.migrations';
import {
  OFFLINE_DATABASE_INDEXES,
  OFFLINE_DATABASE_TABLES,
} from './offlineDatabase.schema';

export const bootstrapOfflineDatabase = async (db: SQLite.SQLiteDatabase): Promise<void> => {
  await db.executeSql('PRAGMA foreign_keys = ON');
  await createTables(db);
  await runMigrations(db);
  await ensureCurrentSchemaColumns(db);
  await createIndexes(db);
  await verifyPhotosMetadataTable(db);
};

const createTables = async (db: SQLite.SQLiteDatabase): Promise<void> => {
  try {
    await db.transaction((tx: Transaction) => {
      OFFLINE_DATABASE_TABLES.forEach((tableSQL) => {
        tx.executeSql(tableSQL);
      });
    });

  } catch (error) {
    console.warn('[OfflineDB] Error creating tables:', error);
    throw new Error(`Failed to create database tables: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

const CURRENT_SCHEMA_COLUMNS = [
  ['reviews', 'environment_key', "environment_key TEXT NOT NULL DEFAULT 'legacy'"],
  ['reviews', 'address', 'address TEXT'],
  ['reviews', 'on_demand_projects', 'on_demand_projects TEXT'],
  ['reviews', 'member_name', 'member_name TEXT'],
  ['reviews', 'member_email', 'member_email TEXT'],
  ['reviews', 'home_manager_name', 'home_manager_name TEXT'],
  ['reviews', 'home_manager_email', 'home_manager_email TEXT'],
  ['responses', 'environment_key', "environment_key TEXT NOT NULL DEFAULT 'legacy'"],
  ['responses', 'floor', 'floor TEXT'],
  ['responses', 'item_snapshot', 'item_snapshot TEXT'],
  ['responses', 'findings', 'findings TEXT'],
  ['photos_queue', 'note', 'note TEXT'],
  ['photos_metadata', 'note', 'note TEXT'],
  ['photos_metadata', 'note_sync_status', "note_sync_status TEXT NOT NULL DEFAULT 'synced'"],
  ['photos_metadata', 'note_sync_error', 'note_sync_error TEXT'],
  ['offline_on_demand_items', 'sync_error', 'sync_error TEXT'],
  ['offline_on_demand_items', 'synced_at', 'synced_at TEXT'],
  ['shared_item_links', 'include_floor', 'include_floor INTEGER NOT NULL DEFAULT 1'],
  ['reviews', 'last_local_mutation_at', 'last_local_mutation_at TEXT'],
  ['reviews', 'last_server_seen_at', 'last_server_seen_at TEXT'],
  ['reviews', 'local_revision', 'local_revision INTEGER NOT NULL DEFAULT 0'],
  ['reviews', 'server_revision', 'server_revision INTEGER NOT NULL DEFAULT 0'],
  ['reviews', 'conflict_status', "conflict_status TEXT NOT NULL DEFAULT 'none'"],
  ['responses', 'last_local_mutation_at', 'last_local_mutation_at TEXT'],
  ['responses', 'last_server_seen_at', 'last_server_seen_at TEXT'],
  ['responses', 'local_revision', 'local_revision INTEGER NOT NULL DEFAULT 0'],
  ['responses', 'server_revision', 'server_revision INTEGER NOT NULL DEFAULT 0'],
  ['responses', 'conflict_status', "conflict_status TEXT NOT NULL DEFAULT 'none'"],
  ['photos_queue', 'environment_key', "environment_key TEXT NOT NULL DEFAULT 'legacy'"],
  ['offline_rooms', 'environment_key', "environment_key TEXT NOT NULL DEFAULT 'legacy'"],
  ['offline_on_demand_items', 'environment_key', "environment_key TEXT NOT NULL DEFAULT 'legacy'"],
  ['id_mappings', 'environment_key', "environment_key TEXT NOT NULL DEFAULT 'legacy'"],
  ['offline_mutation_journal', 'environment_key', "environment_key TEXT NOT NULL DEFAULT 'legacy'"],
  ['review_recovery_snapshots', 'environment_key', "environment_key TEXT NOT NULL DEFAULT 'legacy'"],
] as const;

const ensureCurrentSchemaColumns = async (db: SQLite.SQLiteDatabase): Promise<void> => {
  try {
    for (const [tableName, columnName, columnDefinition] of CURRENT_SCHEMA_COLUMNS) {
      await addColumnIfMissing(db, tableName, columnName, columnDefinition);
    }
  } catch (error) {
    console.warn('[OfflineDB] Error ensuring current schema columns:', error);
    throw new Error(`Failed to ensure current schema columns: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

const createIndexes = async (db: SQLite.SQLiteDatabase): Promise<void> => {
  try {
    await db.transaction((tx: Transaction) => {
      OFFLINE_DATABASE_INDEXES.forEach((indexSQL) => {
        tx.executeSql(indexSQL);
      });
    });
  } catch (error) {
    console.warn('[OfflineDB] Error creating indexes:', error);
    throw new Error(`Failed to create database indexes: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

const runMigrations = async (db: SQLite.SQLiteDatabase): Promise<void> => {
  try {
    const result = await db.executeSql(
      'SELECT value FROM db_metadata WHERE key = ?',
      ['version']
    );

    let currentVersion = 0;
    if (result[0].rows.length > 0) {
      const parsedVersion = parseInt(result[0].rows.item(0).value, 10);
      currentVersion = Number.isFinite(parsedVersion) ? parsedVersion : 0;
    }

    for (const migrationVersion of getPendingMigrationVersions(currentVersion, OFFLINE_DATABASE_VERSION)) {
      await migrateTo(db, migrationVersion);
    }
  } catch (error) {
    console.error('[OfflineDB] ❌ Migration failed:', {
      error: error instanceof Error ? error.message : 'Unknown error',
      stack: error instanceof Error ? error.stack : undefined,
      currentVersion: 'unknown',
      targetVersion: OFFLINE_DATABASE_VERSION
    });
    throw new Error(`Failed to run database migrations: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

const migrateTo = async (db: SQLite.SQLiteDatabase, version: number): Promise<void> => {
  try {
    if (version === 2) {
      try {
        await addColumnIfMissing(db, 'reviews', 'address', 'address TEXT');
      } catch (error) {
        console.error('[OfflineDB] Error checking/adding address column:', error);
      }
    }

    if (version === 3) {
      try {
        await addColumnIfMissing(db, 'offline_on_demand_items', 'sync_error', 'sync_error TEXT');
        await addColumnIfMissing(db, 'offline_on_demand_items', 'synced_at', 'synced_at TEXT');
      } catch (error) {
        console.error('[OfflineDB] Error checking/adding columns to offline_on_demand_items:', error);
      }
    }

    if (version === 4) {
      try {
        await db.executeSql(
          `UPDATE rooms_cache SET property_id = NULL
           WHERE property_id IN ('default', 'defaults') OR property_id = ''`
        );
      } catch (error) {
        console.error('[OfflineDB] Error normalizing room property_ids:', error);
      }
    }

    if (version === 5) {
      try {
        const tables = ['shared_category_links', 'shared_item_links', 'shared_question_mappings', 'review_response_provenance'];
        for (const tableName of tables) {
          await tableExists(db, tableName);
        }
      } catch (error) {
        console.error('[OfflineDB] Error verifying shared items tables:', error);
      }
    }

    if (version === 6) {
      try {
        await addColumnIfMissing(db, 'responses', 'floor', 'floor TEXT');
      } catch (error) {
        console.error('[OfflineDB] Error adding floor column:', error);
        throw error;
      }
    }

    if (version === 7) {
      try {
        await addColumnIfMissing(db, 'shared_item_links', 'include_floor', 'include_floor INTEGER NOT NULL DEFAULT 1');
      } catch (error) {
        console.error('[OfflineDB] Error adding include_floor column:', error);
        throw error;
      }
    }

    if (version === 8) {
      try {
        await addColumnIfMissing(db, 'responses', 'item_snapshot', 'item_snapshot TEXT');
      } catch (error) {
        console.error('[OfflineDB] Error adding item_snapshot column:', error);
        throw error;
      }

      try {
        await db.executeSql(
          `CREATE TABLE IF NOT EXISTS review_template_snapshots (
            review_id INTEGER PRIMARY KEY,
            template_data TEXT NOT NULL,
            cached_at TEXT NOT NULL DEFAULT CURRENT_TIMESTAMP,
            updated_at TEXT NOT NULL DEFAULT CURRENT_TIMESTAMP,
            FOREIGN KEY (review_id) REFERENCES reviews(id) ON DELETE CASCADE
          )`
        );
      } catch (error) {
        console.error('[OfflineDB] Error creating review_template_snapshots table:', error);
        throw error;
      }
    }

    if (version === 9) {
      try {
        await addColumnIfMissing(db, 'reviews', 'on_demand_projects', 'on_demand_projects TEXT');
      } catch (error) {
        console.error('[OfflineDB] Error adding on_demand_projects column:', error);
        throw error;
      }
    }

    if (version === 10) {
      try {
        const metadataColumns = [
          ['last_local_mutation_at', 'last_local_mutation_at TEXT'],
          ['last_server_seen_at', 'last_server_seen_at TEXT'],
          ['local_revision', 'local_revision INTEGER NOT NULL DEFAULT 0'],
          ['server_revision', 'server_revision INTEGER NOT NULL DEFAULT 0'],
          ['conflict_status', "conflict_status TEXT NOT NULL DEFAULT 'none'"],
        ] as const;

        for (const [columnName, columnDefinition] of metadataColumns) {
          await addColumnIfMissing(db, 'reviews', columnName, columnDefinition);
          await addColumnIfMissing(db, 'responses', columnName, columnDefinition);
        }

        await db.executeSql(
          `CREATE TABLE IF NOT EXISTS offline_mutation_journal (
            id INTEGER PRIMARY KEY AUTOINCREMENT,
            entity_type TEXT NOT NULL,
            entity_id INTEGER,
            review_id INTEGER,
            item_id INTEGER,
            mutation_type TEXT NOT NULL,
            payload TEXT,
            previous_snapshot TEXT,
            sync_result TEXT,
            status TEXT NOT NULL DEFAULT 'pending',
            tombstone INTEGER NOT NULL DEFAULT 0,
            created_at TEXT NOT NULL DEFAULT CURRENT_TIMESTAMP,
            updated_at TEXT NOT NULL DEFAULT CURRENT_TIMESTAMP
          )`
        );

        await db.executeSql(
          `CREATE TABLE IF NOT EXISTS review_recovery_snapshots (
            id INTEGER PRIMARY KEY AUTOINCREMENT,
            review_id INTEGER NOT NULL,
            reason TEXT NOT NULL,
            snapshot TEXT NOT NULL,
            created_at TEXT NOT NULL DEFAULT CURRENT_TIMESTAMP
          )`
        );

        const indexes = [
          'CREATE INDEX IF NOT EXISTS idx_reviews_conflict_status ON reviews(conflict_status)',
          'CREATE INDEX IF NOT EXISTS idx_responses_review_item ON responses(review_id, item_id)',
          'CREATE INDEX IF NOT EXISTS idx_responses_conflict_status ON responses(conflict_status)',
          'CREATE INDEX IF NOT EXISTS idx_offline_mutation_journal_review_status ON offline_mutation_journal(review_id, status)',
          'CREATE INDEX IF NOT EXISTS idx_offline_mutation_journal_entity ON offline_mutation_journal(entity_type, entity_id)',
          'CREATE INDEX IF NOT EXISTS idx_review_recovery_snapshots_review_created ON review_recovery_snapshots(review_id, created_at)',
        ];

        for (const indexSql of indexes) {
          await db.executeSql(indexSql);
        }
      } catch (error) {
        console.error('[OfflineDB] Error adding recovery metadata/journal tables:', error);
        throw error;
      }
    }

    if (version === 11) {
      try {
        const environmentTables = [
          'reviews',
          'responses',
          'photos_queue',
          'offline_rooms',
          'offline_on_demand_items',
          'id_mappings',
          'offline_mutation_journal',
          'review_recovery_snapshots',
        ];

        for (const tableName of environmentTables) {
          await addColumnIfMissing(db, tableName, 'environment_key', "environment_key TEXT NOT NULL DEFAULT 'legacy'");
        }
      } catch (error) {
        console.error('[OfflineDB] Error adding environment columns:', error);
        throw error;
      }
    }

    if (version === 12) {
      const reviewContactColumns = [
        ['member_name', 'member_name TEXT'],
        ['member_email', 'member_email TEXT'],
        ['home_manager_name', 'home_manager_name TEXT'],
        ['home_manager_email', 'home_manager_email TEXT'],
      ] as const;

      for (const [columnName, columnDefinition] of reviewContactColumns) {
        await addColumnIfMissing(db, 'reviews', columnName, columnDefinition);
      }
      await addColumnIfMissing(db, 'responses', 'findings', 'findings TEXT');
      await addColumnIfMissing(db, 'photos_queue', 'note', 'note TEXT');
      await addColumnIfMissing(db, 'photos_metadata', 'note', 'note TEXT');
      await addColumnIfMissing(
        db,
        'photos_metadata',
        'note_sync_status',
        "note_sync_status TEXT NOT NULL DEFAULT 'synced'"
      );
      await addColumnIfMissing(db, 'photos_metadata', 'note_sync_error', 'note_sync_error TEXT');
    }

    await db.executeSql(
      'INSERT OR REPLACE INTO db_metadata (key, value) VALUES (?, ?)',
      ['version', version.toString()]
    );
  } catch (error) {
    console.error('[OfflineDB] Error migrating database:', error);
    throw new Error(`Failed to migrate database to version ${version}: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

const verifyPhotosMetadataTable = async (db: SQLite.SQLiteDatabase): Promise<void> => {
  try {
    const tableInfo = await db.executeSql('PRAGMA table_info(photos_metadata)');
    if (tableInfo[0].rows.length === 0) {
      console.error('[OfflineDB] photos_metadata table does not exist!');
    }
  } catch (pragmaError) {
    console.error('[OfflineDB] Failed to verify photos_metadata table:', pragmaError);
  }
};
